import React, { useState } from 'react';
import { Github, ExternalLink, Code, Zap, Shield, Database, Brain, Settings } from 'lucide-react';

const Projects = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const projects = [
    {
      title: "DocuMind AI – RAG Documentation Assistant",
      description: "A production-ready RAG system that processes any document format (MD, PDF, DOCX, TXT) using advanced RAG with FAISS Vector DB. Provides instant, accurate answers with source citations and falls back to DevDocs.io for programming queries.",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop",
      technologies: ["FastAPI", "Python", "FAISS", "RAG", "GPT-4-turbo", "sentence-transformers", "React"],
      category: "ai",
      highlights: [
        "Engineered RAG system using FAISS vector database and GPT-4-turbo",
        "Advanced retrieval with sentence-transformers and vector similarity matching",
        "Instant answers with source citations from multiple document formats",
        "Expertise in relevance thresholds and streaming LLM responses"
      ],
      github: "#",
      demo: "https://www.linkedin.com/posts/swastik-nanda-gen-ai_genai-genaiapplication-llmappdevelopment-activity-7382493411364376576-Vtdm?utm_source=share&utm_medium=member_desktop&rcm=ACoAAA95ERUBStIgW9NztPhT7nNenHtYozkFamA",
      featured: true,
      period: "September 2025 - Present",
      enableViewDetails: true
    },
    {
      title: "Doctor Assistant Chatbot – AI Booking Platform",
      description: "Conversational AI booking system using OpenAI Function Calling (GPT-3.5-turbo) that automates appointment scheduling across locations, specializations, and time slots with natural language processing.",
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&h=400&fit=crop",
      technologies: ["Python", "React", "MySQL", "OpenAI API", "GPT-3.5-turbo", "Function Calling"],
      category: "ai",
      highlights: [
        "Automated appointment scheduling using OpenAI Function Calling",
        "Advanced prompt engineering with date normalization and input sanitization",
        "Natural language query handling with structured data extraction",
        "Multi-step workflows with real-time validation"
      ],
      github: "#",
      demo: "https://www.linkedin.com/posts/swastik-nanda-gen-ai_ai-openai-healthcare-activity-7377422015621718016-v46-?utm_source=share&utm_medium=member_desktop&rcm=ACoAAA95ERUBStIgW9NztPhT7nNenHtYozkFamA",
      featured: true,
      period: "September 2025 - Present",
      enableViewDetails: true
    },
    {
      title: "LinkedIn Content Generator – AI Workflow Automation",
      description: "AI-powered content generation platform using bolt.new, n8n workflow automation, and LLM APIs with LinkedIn integration. Generates customized LinkedIn-ready posts based on theme, tone, and length parameters.",
      image: "https://customer-assets.emergentagent.com/job_swastik-portfolio-1/artifacts/g80rjz89_Gemini_Generated_Image_vuo1s9vuo1s9vuo1.png",
      technologies: ["bolt.new", "n8n", "LLM APIs", "LinkedIn API", "Workflow Automation"],
      category: "ai",
      highlights: [
        "AI-powered content generation with customizable parameters",
        "Theme, tone, and length-based post creation with prompt engineering",
        "One-click LinkedIn publishing with direct integration",
        "Rapid prototyping through no-code/low-code tools"
      ],
      github: "#",
      demo: "https://www.linkedin.com/posts/swastik-nanda-gen-ai_ai-nocode-automation-activity-7374879479418019840-D0iY?utm_source=share&utm_medium=member_desktop&rcm=ACoAAA95ERUBStIgW9NztPhT7nNenHtYozkFamA",
      featured: true,
      period: "September 2025 - Present",
      enableViewDetails: true
    },
    {
      title: "EWACS - Early Warning And Control System",
      description: "A preventative self-healing automation tool for SAP PI environment that reduced manual intervention by 90%. Built from idea generation to maintenance, handling failures without manual intervention.",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop",
      technologies: ["Java", "SAP PI", "Automation", "System Integration", "Monitoring"],
      category: "automation",
      highlights: [
        "90% reduction in manual resolution time",
        "Prevents business disruptions",
        "Eliminates human errors",
        "End-to-end automation solution"
      ],
      github: "#",
      demo: "https://nandaswastik.medium.com/ewacs-early-warning-and-control-system-a-preventative-self-healing-tool-for-sap-pi-environment-9fcfd90d5dfa",
      featured: false
    },
    {
      title: "PCI-Compliant Payment Processing Module",
      description: "Developed a secure payment feature on Amdocs CRM enabling Customer Service Representatives to process payments via chat while maintaining PCI compliance for sensitive data handling.",
      image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=600&h=400&fit=crop",
      technologies: ["Java", "Amdocs CRM", "PCI Compliance", "Payment Processing", "Security"],
      category: "security",
      highlights: [
        "PCI-compliant security implementation",
        "Seamless payment processing via chat",
        "Enhanced customer experience",
        "Secure data handling protocols"
      ],
      github: "#",
      demo: "#",
      featured: false
    },
    {
      title: "Mass Configuration Web Application",
      description: "Extended and maintained a JEE web application for mass configuration in System Landscape Directory and Integration Directory, reducing time consumption by 70%.",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop",
      technologies: ["Java EE", "Web Development", "SAP PI", "System Configuration", "Integration"],
      category: "web",
      highlights: [
        "70% reduction in configuration time",
        "Mass configuration capabilities",
        "Streamlined system management",
        "Enhanced operational efficiency"
      ],
      github: "#",
      demo: "#",
      featured: false
    },
    {
      title: "ITSM Incident Tracking Dashboard",
      description: "Built a comprehensive dashboard to track all incoming incidents and requests, helping the team deliver solutions on time and avoid business disruption.",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop",
      technologies: ["Dashboard Development", "ITSM", "Data Visualization", "Incident Management"],
      category: "web",
      highlights: [
        "Real-time incident tracking",
        "Improved solution delivery time",
        "Comprehensive request management",
        "Business disruption prevention"
      ],
      github: "#",
      demo: "#",
      featured: false
    },
    {
      title: "GPRS Controlled Home Automation System",
      description: "Designed and implemented home automation system using GPRS module and FPGA with Verilog FSM. Operations controlled via mobile SMS with UART communication.",
      image: "https://images.unsplash.com/photo-1702390796625-6dd9b46b1c0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxJb1QlMjBob21lJTIwY29udHJvbHxlbnwwfHx8fDE3NTc0MzAyNDN8MA&ixlib=rb-4.1.0&q=85",
      technologies: ["FPGA", "Verilog", "GPRS", "UART", "Home Automation", "Embedded Systems"],
      category: "iot",
      highlights: [
        "FPGA-based system implementation",
        "SMS-controlled operations",
        "UART communication protocol",
        "Won Best B.Tech Project Award"
      ],
      github: "#",
      demo: "#",
      featured: false
    },
    {
      title: "AI-Powered Pre-Analysis Tool",
      description: "Developed a pre-analysis tool leveraging AI capabilities that reduced issue analysis time by 10 minutes, enhancing team productivity and response times.",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop",
      technologies: ["AI", "Analysis Automation", "Productivity Tools", "Machine Learning"],
      category: "ai",
      highlights: [
        "10-minute reduction in analysis time",
        "AI-powered automation",
        "Enhanced team productivity",
        "Intelligent issue detection"
      ],
      github: "#",
      demo: "#",
      featured: false
    }
  ];

  const categories = [
    { id: 'all', name: 'All Projects', icon: <Code className="w-4 h-4" /> },
    { id: 'automation', name: 'Automation', icon: <Zap className="w-4 h-4" /> },
    { id: 'security', name: 'Security', icon: <Shield className="w-4 h-4" /> },
    { id: 'web', name: 'Web Apps', icon: <Database className="w-4 h-4" /> },
    { id: 'ai', name: 'AI/ML', icon: <Brain className="w-4 h-4" /> },
    { id: 'iot', name: 'IoT/Embedded', icon: <Settings className="w-4 h-4" /> }
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  const featuredProjects = projects.filter(project => project.featured);

  return (
    <section id="projects" className="py-20 bg-slate-900">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Featured <span className="text-blue-400">Projects</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-purple-500 mx-auto mb-8"></div>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Innovative solutions built with cutting-edge technologies, showcasing expertise in automation, security, and system integration
          </p>
        </div>

        {/* Featured Projects */}
        <div className="mb-16">
          <h3 className="text-2xl font-semibold text-white mb-8 flex items-center">
            <Zap className="w-6 h-6 text-blue-400 mr-3" />
            Spotlight Projects
          </h3>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {featuredProjects.map((project, index) => (
              <div
                key={index}
                className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden hover:border-blue-500/50 transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-blue-500/10 group"
              >
                {project.enableViewDetails ? (
                  <a href={project.demo} target="_blank" rel="noopener noreferrer" className="block">
                    <div className="relative overflow-hidden cursor-pointer">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                      <div className="absolute top-4 right-4">
                        <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-xs font-medium border border-blue-500/30">
                          Featured
                        </span>
                      </div>
                    </div>
                  </a>
                ) : (
                  <div className="relative overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                    <div className="absolute top-4 right-4">
                      <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-xs font-medium border border-blue-500/30">
                        Featured
                      </span>
                    </div>
                  </div>
                )}
                
                <div className="p-6">
                  {project.enableViewDetails ? (
                    <a href={project.demo} target="_blank" rel="noopener noreferrer">
                      <h4 className="text-xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors duration-300 cursor-pointer">
                        {project.title}
                      </h4>
                    </a>
                  ) : (
                    <h4 className="text-xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors duration-300">
                      {project.title}
                    </h4>
                  )}
                  
                  <p className="text-gray-400 mb-4 leading-relaxed">
                    {project.description}
                  </p>
                  
                  <div className="mb-4">
                    <h5 className="text-sm font-semibold text-blue-400 mb-2">Key Highlights:</h5>
                    <ul className="space-y-1">
                      {project.highlights.slice(0, 3).map((highlight, hIndex) => (
                        <li key={hIndex} className="text-gray-400 text-sm flex items-start">
                          <span className="text-blue-400 mr-2 mt-1 text-xs">▪</span>
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="mb-6">
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.slice(0, 4).map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className="bg-slate-700/50 text-blue-400 px-3 py-1 rounded-full text-xs border border-blue-500/30"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    {project.enableViewDetails ? (
                      <a
                        href={project.demo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center transition-all duration-300"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        View Details
                      </a>
                    ) : (
                      <button
                        disabled
                        className="flex-1 bg-gray-600 cursor-not-allowed text-gray-400 px-4 py-2 rounded-lg font-medium flex items-center justify-center opacity-50"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        View Details
                      </button>
                    )}
                    <button 
                      disabled
                      className="border border-gray-600 text-gray-400 cursor-not-allowed px-4 py-2 rounded-lg font-medium flex items-center justify-center opacity-50"
                    >
                      <Github className="w-4 h-4 mr-2" />
                      Code
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Project Categories Filter */}
        <div className="mb-12">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveFilter(category.id)}
                className={`flex items-center px-4 py-2 rounded-full font-medium transition-all duration-300 ${
                  activeFilter === category.id
                    ? 'bg-blue-500 text-white'
                    : 'bg-slate-700/50 text-gray-300 hover:bg-slate-600/50 hover:text-blue-400'
                }`}
              >
                {category.icon}
                <span className="ml-2">{category.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* All Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.filter(project => !project.featured).map((project, index) => (
            <div
              key={index}
              className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden hover:border-blue-500/50 transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-blue-500/10 group"
            >
              {project.enableViewDetails ? (
                <a href={project.demo} target="_blank" rel="noopener noreferrer" className="block">
                  <div className="relative overflow-hidden cursor-pointer">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-40 object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                  </div>
                </a>
              ) : (
                <div className="relative overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-40 object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                </div>
              )}
              
              <div className="p-5">
                {project.enableViewDetails ? (
                  <a href={project.demo} target="_blank" rel="noopener noreferrer">
                    <h4 className="text-lg font-bold text-white mb-2 group-hover:text-blue-400 transition-colors duration-300 cursor-pointer">
                      {project.title}
                    </h4>
                  </a>
                ) : (
                  <h4 className="text-lg font-bold text-white mb-2 group-hover:text-blue-400 transition-colors duration-300">
                    {project.title}
                  </h4>
                )}
                
                <p className="text-gray-400 mb-3 text-sm leading-relaxed line-clamp-3">
                  {project.description}
                </p>
                
                <div className="mb-4">
                  <div className="flex flex-wrap gap-1">
                    {project.technologies.slice(0, 3).map((tech, techIndex) => (
                      <span
                        key={techIndex}
                        className="bg-slate-700/50 text-blue-400 px-2 py-1 rounded text-xs border border-blue-500/30"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="flex gap-2">
                  {project.enableViewDetails ? (
                    <a
                      href={project.demo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center border border-blue-500 transition-all duration-300"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      View
                    </a>
                  ) : (
                    <button
                      disabled
                      className="flex-1 bg-gray-600 cursor-not-allowed text-gray-400 px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center border border-gray-600 opacity-50"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      View
                    </button>
                  )}
                  <button 
                    disabled
                    className="border border-gray-600 text-gray-400 cursor-not-allowed px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center opacity-50"
                  >
                    <Github className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Project Impact Stats */}
        <div className="mt-16 bg-gradient-to-r from-slate-800/50 to-slate-700/50 border border-slate-600/50 rounded-xl p-8">
          <h3 className="text-2xl font-semibold text-white mb-8 text-center">
            Project Impact
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl md:text-4xl font-bold text-blue-400 mb-2">9+</div>
              <p className="text-gray-400 text-sm">Major Projects</p>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-purple-400 mb-2">90%</div>
              <p className="text-gray-400 text-sm">Automation Impact</p>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-green-400 mb-2">70%</div>
              <p className="text-gray-400 text-sm">Time Reduction</p>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-orange-400 mb-2">8+</div>
              <p className="text-gray-400 text-sm">Technologies</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;